import { type QueryKey, useQuery } from '@tanstack/react-query';
import { isObject } from 'lodash';

import { Clips } from '@air/api';
import { type Clip, type ClipGetOptions } from '@air/api/types';

export type ClipGetOptionsWithBoards = ClipGetOptions & { withBoards: true };

export const defaultClipGetOptions: ClipGetOptionsWithBoards = {
  withBoards: true,
  withAncestorsAndThumbnails: true,
  withOpenDiscussionStatus: true,
  numBoardThumbnails: 1,
};

const CLIP_WITHOUT_WORKSPACE_ID_KEY = 'clips';

export type ClipWithoutWorkspaceIdKey = [
  typeof CLIP_WITHOUT_WORKSPACE_ID_KEY,
  Partial<ClipGetOptions> & { clipId: string },
];

export const getClipWithoutWorkspaceIdKey = ({
  clipId,
  options,
}: {
  clipId: string;
  options?: ClipGetOptions;
}): ClipWithoutWorkspaceIdKey => [CLIP_WITHOUT_WORKSPACE_ID_KEY, { clipId, ...options }];

export const isClipWithoutWorkspaceIdKey = (key: QueryKey): key is ClipWithoutWorkspaceIdKey => {
  return Array.isArray(key) && key[0] === CLIP_WITHOUT_WORKSPACE_ID_KEY && isObject(key[1]);
};

export type UseGetClipWithNoWorkspaceIdProps<Options extends ClipGetOptions> = {
  clipId?: Clip['id'];
  options?: Options;
};

export const useGetClipWithNoWorkspaceId = <Options extends ClipGetOptions>({
  clipId,
  options,
}: UseGetClipWithNoWorkspaceIdProps<Options>) => {
  return useQuery({
    queryKey: clipId ? getClipWithoutWorkspaceIdKey({ clipId, options }) : [],

    queryFn: () => {
      if (!clipId) {
        throw new Error('clipId is required');
      }

      return Clips.getWithNoWorkspaceId({ id: clipId, options });
    },
    enabled: !!clipId,
  });
};
